import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog.js';
import { pluralize } from '@/lib/string-utils.js';
import type { CoachPoolFilters, CoachPoolWithMatchCount } from '@airreading/common/data';

import { CoachPoolFilterChips } from './coach-pool-filter-chips.js';

export interface CoachPoolSaveReview {
  mode: 'create' | 'edit';
  id?: string;
  title: string;
  description: string;
  isActive: boolean;
  filters: CoachPoolFilters;
  /** Teachers matching the saved pool before this edit. */
  savedTeachersCount: number;
}

interface DiscardCoachPoolChangesDialogProps {
  open: boolean;
  poolTitle: string;
  onOpenChange: (open: boolean) => void;
  onDiscard: () => void;
}

interface SaveCoachPoolDialogProps {
  review: CoachPoolSaveReview | null;
  /** Teachers matching the draft filters, from the availability table. */
  matchingTeachersCount: number | undefined;
  isSaving: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

interface SetCoachPoolActiveDialogProps {
  pool: CoachPoolWithMatchCount | null;
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const discardDataTestId = 'teachers-availabilities-modal-discard-pool';

export const DiscardCoachPoolChangesDialog = ({
  open,
  poolTitle,
  onOpenChange,
  onDiscard,
}: DiscardCoachPoolChangesDialogProps) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={discardDataTestId}>
        <DialogHeader>
          <DialogTitle>Discard unsaved changes?</DialogTitle>
          <DialogDescription className="pt-2">
            Your changes to <span className="font-semibold text-foreground">{poolTitle}</span> have not been saved.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            data-testid={`${discardDataTestId}-button-keep-editing`}
            onClick={() => onOpenChange(false)}
          >
            Keep editing
          </Button>
          <Button type="button" data-testid={`${discardDataTestId}-button-discard`} onClick={onDiscard}>
            Discard changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const saveDataTestId = 'teachers-availabilities-modal-save-pool';

const membershipChangeLabel = (review: CoachPoolSaveReview, matchingTeachersCount: number): string => {
  if (review.mode === 'create') {
    return `${matchingTeachersCount} added to this pool.`;
  }
  const change = matchingTeachersCount - review.savedTeachersCount;
  if (change > 0) {
    return `${change} more than the saved pool (${review.savedTeachersCount}).`;
  }
  if (change < 0) {
    return `${-change} fewer than the saved pool (${review.savedTeachersCount}).`;
  }
  return `Same count as the saved pool.`;
};

/** Review shown before creating or updating a teacher pool */
export const SaveCoachPoolDialog = ({
  review,
  matchingTeachersCount,
  isSaving,
  onOpenChange,
  onConfirm,
}: SaveCoachPoolDialogProps) => {
  const status = review?.isActive ? 'Active' : 'Inactive';
  const teacherLabel = pluralize(matchingTeachersCount ?? 0, 'teacher', 'teachers');

  return (
    <Dialog open={review != null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid={saveDataTestId}>
        {review ? (
          <>
            <DialogHeader>
              <DialogTitle>Save changes to {review.title}?</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-4">
              <section className="flex flex-col gap-3 rounded-md border bg-muted/40 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold">{review.title}</h3>
                  <Badge
                    className={
                      review.isActive
                        ? 'border-transparent bg-green-100 text-green-700 hover:bg-green-100'
                        : 'border-transparent bg-blue-100 text-blue-700 hover:bg-blue-100'
                    }
                  >
                    {status}
                  </Badge>
                </div>
                <CoachPoolFilterChips
                  filters={{ ...review.filters, untakenTeachersOnly: undefined, coachAvailabilityType: undefined }}
                />
              </section>
              {matchingTeachersCount == null ? (
                <p className="text-sm text-muted-foreground" role="status">
                  Loading matching teachers
                </p>
              ) : (
                <p className="text-sm">
                  <span className="font-semibold">
                    {matchingTeachersCount} matching {teacherLabel}
                  </span>
                  <span className="text-muted-foreground">
                    {' '}
                    · {membershipChangeLabel(review, matchingTeachersCount)}
                  </span>
                </p>
              )}
              <DialogDescription className="space-y-3 text-sm text-muted-foreground">
                <span className="block">
                  These are membership changes to this pool only. Removed teachers can remain reserved through other
                  active pools.
                </span>
                <span className="block">
                  This pool will {review.mode === 'edit' ? 'remain' : 'be'}{' '}
                  <span className="font-semibold text-foreground">{status}</span>. Other pools are unchanged.
                </span>
              </DialogDescription>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                disabled={isSaving}
                data-testid={`${saveDataTestId}-button-cancel`}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                loading={isSaving}
                data-testid={`${saveDataTestId}-button-submit`}
                onClick={onConfirm}
              >
                Save changes
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

/** Dialog to confirm the active state of a coach pool */
export const SetCoachPoolActiveDialog = ({
  pool,
  isPending,
  onOpenChange,
  onConfirm,
}: SetCoachPoolActiveDialogProps) => {
  const activating = pool ? !pool.isActive : false;
  const action = activating ? 'Activate' : 'Deactivate';
  const teacherLabel = pluralize(pool?.matchingTeachersCount ?? 0, 'teacher', 'teachers');
  const filtersHeadingId = pool ? `teacher-pool-${pool.id}-applied-filters` : undefined;
  const dataTestId = pool ? `teacher-pool-modal-${activating ? 'activate' : 'deactivate'}` : undefined;

  return (
    <Dialog open={pool != null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl" data-testid={dataTestId}>
        {pool && (
          <>
            <DialogHeader>
              <DialogTitle>{action} reserved teacher pool</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <p className="text-sm">
                <span className="font-semibold">Pool name:</span> {pool.title}
              </p>
              <p className="text-sm">
                <span className="font-semibold">Description (optional):</span> {pool.description?.trim()}
              </p>
              <h3 className="text-base font-semibold">
                {pool.matchingTeachersCount} matching {teacherLabel}
              </h3>
              <section
                aria-labelledby={filtersHeadingId}
                className="flex flex-col gap-2 rounded-md border bg-muted/40 p-3"
              >
                <h4 id={filtersHeadingId} className="text-sm font-semibold">
                  Applied filters
                </h4>
                <CoachPoolFilterChips
                  filters={pool}
                  emptyState={<p className="text-sm text-muted-foreground">No filters applied.</p>}
                />
              </section>
              {activating ? (
                <DialogDescription className="space-y-2">
                  <span className="block">[Test] teachers are automatically excluded.</span>
                  <span className="block">
                    Matches update automatically when teacher data changes. Its teachers are assigned only when no other
                    eligible teachers are available.
                  </span>
                </DialogDescription>
              ) : (
                <DialogDescription>
                  Teachers matching this pool will return to normal priority unless they also match another active pool.
                </DialogDescription>
              )}
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="secondary"
                disabled={isPending}
                data-testid={dataTestId ? `${dataTestId}-button-cancel` : undefined}
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                loading={isPending}
                data-testid={dataTestId ? `${dataTestId}-button-submit` : undefined}
                onClick={onConfirm}
              >
                {action}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
